import { z } from 'zod';
import { isoDateSchema } from '../common.js';
import { brandingSchema, loginPageSchema } from './branding.js';
import { layoutSchema } from './layout.js';
import { navPageSchema } from './pages.js';
import { siteCustomisationSchema } from './site.js';
import { themeSchema } from './theme.js';

// ---- tenants -------------------------------------------------------------------------------

export const tenantSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and dashes.');

export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?::\d{1,5})?$/,
    'Enter a hostname such as panel.example.com.',
  );

export const tenantSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  hostnames: z.array(z.string()),
  isDefault: z.boolean(),
  admins: z.array(z.object({ uuid: z.string(), username: z.string() })),
  customerCount: z.number().int(),
  createdAt: isoDateSchema,
});
export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantSchema = z.object({
  slug: tenantSlugSchema,
  name: z.string().trim().min(1).max(120),
  hostnames: z.array(hostnameSchema).max(10).default([]),
});

export const updateTenantSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  hostnames: z.array(hostnameSchema).max(10).optional(),
  /** Replace the administrators who manage this tenant (user UUIDs). */
  adminUuids: z.array(z.uuid()).max(100).optional(),
});

/** A tenant as the studio's switcher lists it. */
export const managedTenantSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  hostnames: z.array(z.string()),
});
export type ManagedTenant = z.infer<typeof managedTenantSchema>;

export const managedTenantsSchema = z.object({
  tenants: z.array(managedTenantSchema),
  /** The caller manages every tenant and the tenant list itself. */
  root: z.boolean(),
});

// ---- assets --------------------------------------------------------------------------------

export const ASSET_KINDS = ['logo', 'favicon', 'background', 'image'] as const;
export const assetKindSchema = z.enum(ASSET_KINDS);
export type AssetKind = z.infer<typeof assetKindSchema>;

export const ASSET_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;
export const MAX_ASSET_BYTES = 5 * 1024 * 1024;
/** Dimension caps per kind: a favicon is tiny, a background can be large. */
export const ASSET_MAX_DIMENSIONS: Record<AssetKind, { width: number; height: number }> = {
  logo: { width: 2048, height: 2048 },
  favicon: { width: 512, height: 512 },
  background: { width: 4096, height: 4096 },
  image: { width: 4096, height: 4096 },
};

export const customisationAssetSchema = z.object({
  id: z.uuid(),
  kind: assetKindSchema,
  filename: z.string(),
  mime: z.string(),
  bytes: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  /** Where the browser loads it from; thumbnails add `?thumb=1`. */
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
  createdAt: isoDateSchema,
});
export type CustomisationAsset = z.infer<typeof customisationAssetSchema>;

export const assetListSchema = z.object({ assets: z.array(customisationAssetSchema) });

// ---- site state ----------------------------------------------------------------------------

export const revisionSchema = z.object({
  id: z.number().int(),
  target: z.enum(['site', 'page']),
  pageId: z.uuid().nullable(),
  version: z.number().int(),
  kind: z.enum(['publish', 'rollback', 'snapshot', 'unpublish']),
  note: z.string().nullable(),
  author: z.object({ uuid: z.string(), username: z.string() }).nullable(),
  createdAt: isoDateSchema,
});
export type CustomisationRevision = z.infer<typeof revisionSchema>;

export const revisionDetailSchema = revisionSchema.extend({
  content: z.unknown(),
});

export const siteStateSchema = z.object({
  tenant: managedTenantSchema,
  draft: siteCustomisationSchema,
  draftUpdatedAt: isoDateSchema.nullable(),
  draftUpdatedBy: z.object({ uuid: z.string(), username: z.string() }).nullable(),
  published: siteCustomisationSchema.nullable(),
  publishedAt: isoDateSchema.nullable(),
  publishedRevisionId: z.number().int().nullable(),
  hasUnpublishedChanges: z.boolean(),
});
export type SiteState = z.infer<typeof siteStateSchema>;

export const saveDraftSchema = z.object({
  site: siteCustomisationSchema,
  /** Optimistic concurrency: the draftUpdatedAt the editor loaded; a mismatch is a 409. */
  baseUpdatedAt: isoDateSchema.nullable().optional(),
});

export const publishSchema = z.object({ note: z.string().trim().max(200).optional() });

// ---- what customers read ------------------------------------------------------------------

/** Resolved logo URLs for the places that show one (nulls fall back to the Hearth mark). */
export const resolvedLogosSchema = z.object({
  primary: z.string().nullable(),
  light: z.string().nullable(),
  dark: z.string().nullable(),
  compact: z.string().nullable(),
  favicon: z.string().nullable(),
  login: z.string().nullable(),
});

export const publicSiteSchema = z.object({
  tenant: z.object({ slug: z.string(), name: z.string() }),
  branding: brandingSchema,
  logos: resolvedLogosSchema,
  theme: themeSchema,
  login: loginPageSchema.extend({ backgroundImageUrl: z.string().nullable() }),
  customCss: z.string(),
  pages: z.array(navPageSchema),
  /** True when this is the draft, shown to an administrator in preview mode. */
  preview: z.boolean(),
  /** Whether anything is published at all (false means Hearth's defaults). */
  customised: z.boolean(),
});
export type PublicSite = z.infer<typeof publicSiteSchema>;

export const publicDashboardSchema = z.object({
  layout: layoutSchema,
  preview: z.boolean(),
});

/** The layouts of every server's overview and console pages. */
export const publicServerLayoutsSchema = z.object({
  overview: layoutSchema,
  console: layoutSchema,
  preview: z.boolean(),
});
export type PublicServerLayouts = z.infer<typeof publicServerLayoutsSchema>;

// ---- AI builder ----------------------------------------------------------------------------

export const AI_PROPOSAL_TARGETS = [
  'dashboard',
  'server-overview',
  'server-console',
  'page',
  'site',
] as const;
export const aiProposalTargetSchema = z.enum(AI_PROPOSAL_TARGETS);
export type AiProposalTarget = z.infer<typeof aiProposalTargetSchema>;

export const createAiProposalSchema = z.object({
  target: aiProposalTargetSchema,
  /** For `page`: the page to change, or omit to have the AI create a new page. */
  pageId: z.uuid().optional(),
  prompt: z.string().trim().min(3, 'Describe what you want.').max(4000),
});

export const aiProposalStatusSchema = z.enum([
  'pending',
  'accepted',
  'rejected',
  'superseded',
  'failed',
]);

/** The AI's output for a `site` target: the parts of the site document it may change. */
export const aiSiteProposalContentSchema = z.object({
  branding: brandingSchema.optional(),
  theme: themeSchema.optional(),
  login: loginPageSchema.optional(),
});

export const aiPageProposalContentSchema = z.object({
  title: z.string().trim().min(1).max(120),
  slug: z.string(),
  description: z.string().max(300).default(''),
  navLabel: z.string().max(40).default(''),
  showInNav: z.boolean().default(true),
  layout: layoutSchema,
  width: z.enum(['narrow', 'default', 'wide']).default('default'),
  showTitle: z.boolean().default(true),
});

export const aiProposalSchema = z.object({
  id: z.uuid(),
  target: aiProposalTargetSchema,
  pageId: z.uuid().nullable(),
  prompt: z.string(),
  summary: z.string(),
  /** What the model couldn't do with the available components, if anything. */
  limitations: z.array(z.string()),
  status: aiProposalStatusSchema,
  /** The proposed document: a layout, a page, or site parts, per `target`. */
  content: z.unknown(),
  /** The draft at proposal time, for comparison. */
  before: z.unknown(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  error: z.string().nullable(),
  author: z.object({ uuid: z.string(), username: z.string() }).nullable(),
  createdAt: isoDateSchema,
  resolvedAt: isoDateSchema.nullable(),
});
export type AiProposal = z.infer<typeof aiProposalSchema>;

export const aiProposalListSchema = z.object({ proposals: z.array(aiProposalSchema) });

export const aiBuilderStatusSchema = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
});
export type AiBuilderStatus = z.infer<typeof aiBuilderStatusSchema>;
